import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiGet, apiPost, apiPatch, apiDelete } from '../utils/api';
import { getToken as getAuthToken } from '../stores/authStore';

const ProjectContext = createContext();

export const useProjects = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProjects must be used within a ProjectProvider');
  }
  return context;
};

export const ProjectProvider = ({ children }) => {
  const [projects, setProjects] = useState([]);
  const [currentProject, setCurrentProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load projects from API
  const fetchProjects = useCallback(async (includeArchived = false) => {
    setLoading(true);
    setError(null);
    try {
      const params = includeArchived ? '?include_archived=true' : '';
      const response = await apiGet(`/projects${params}`);
      const data = await response.json();
      setProjects(data.projects || []);
      return data.projects;
    } catch (err) {
      console.error('Failed to fetch projects:', err);
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Load current project from localStorage on mount
  useEffect(() => {
    // Only fetch projects if user is authenticated
    const token = getAuthToken();
    if (!token) {
      console.log('⚠️  No auth token, skipping project fetch');
      return;
    }

    const savedProjectId = localStorage.getItem('currentProjectId');
    if (savedProjectId) {
      const projectId = parseInt(savedProjectId, 10);
      // We'll set it after projects are loaded
      fetchProjects().then(loadedProjects => {
        const project = loadedProjects.find(p => p.id === projectId);
        if (project) {
          setCurrentProject(project);
        } else {
          // Clear invalid project from localStorage
          localStorage.removeItem('currentProjectId');
        }
      });
    } else {
      fetchProjects();
    }
  }, [fetchProjects]);

  // Create a new project
  const createProject = useCallback(async (projectData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiPost('/projects', {
        project: projectData
      });
      const data = await response.json();

      if (response.ok) {
        const newProject = data.project;
        setProjects(prev => [newProject, ...prev]);
        return { success: true, project: newProject };
      } else {
        setError(data.errors?.join(', ') || 'Failed to create project');
        return { success: false, error: data.errors };
      }
    } catch (err) {
      console.error('Failed to create project:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Update an existing project
  const updateProject = useCallback(async (id, projectData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiPatch(`/projects/${id}`, {
        project: projectData
      });
      const data = await response.json();

      if (response.ok) {
        const updatedProject = data.project;
        setProjects(prev => prev.map(p => p.id === id ? updatedProject : p));

        // Update current project if it's the one being updated
        if (currentProject?.id === id) {
          setCurrentProject(updatedProject);
        }

        return { success: true, project: updatedProject };
      } else {
        setError(data.errors?.join(', ') || 'Failed to update project');
        return { success: false, error: data.errors };
      }
    } catch (err) {
      console.error('Failed to update project:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, [currentProject]);

  // Delete a project
  const deleteProject = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiDelete(`/projects/${id}`);

      if (response.ok) {
        setProjects(prev => prev.filter(p => p.id !== id));

        // Clear current project if it's the one being deleted
        if (currentProject?.id === id) {
          setCurrentProject(null);
          localStorage.removeItem('currentProjectId');
        }

        return { success: true };
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to delete project');
        return { success: false, error: data.error };
      }
    } catch (err) {
      console.error('Failed to delete project:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, [currentProject]);

  // Archive a project
  const archiveProject = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiPost(`/projects/${id}/archive`);
      const data = await response.json();

      if (response.ok) {
        setProjects(prev => prev.map(p =>
          p.id === id ? { ...p, archived: true } : p
        ));

        // Clear current project if it's the one being archived
        if (currentProject?.id === id) {
          setCurrentProject(null);
          localStorage.removeItem('currentProjectId');
        }

        return { success: true };
      } else {
        setError(data.error || 'Failed to archive project');
        return { success: false, error: data.error };
      }
    } catch (err) {
      console.error('Failed to archive project:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, [currentProject]);

  // Unarchive a project
  const unarchiveProject = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiPost(`/projects/${id}/unarchive`);
      const data = await response.json();

      if (response.ok) {
        setProjects(prev => prev.map(p =>
          p.id === id ? { ...p, archived: false } : p
        ));

        return { success: true };
      } else {
        setError(data.error || 'Failed to unarchive project');
        return { success: false, error: data.error };
      }
    } catch (err) {
      console.error('Failed to unarchive project:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Select a project (set as current)
  const selectProject = useCallback((project) => {
    setCurrentProject(project);
    if (project) {
      localStorage.setItem('currentProjectId', project.id.toString());
    } else {
      localStorage.removeItem('currentProjectId');
    }
  }, []);

  // Get project context for AI requests
  const getProjectContext = useCallback(async (projectId, query = null) => {
    try {
      const queryParam = query ? `?query=${encodeURIComponent(query)}` : '';
      const response = await apiGet(`/projects/${projectId}/context${queryParam}`);
      const data = await response.json();
      return data.context;
    } catch (err) {
      console.error('Failed to fetch project context:', err);
      return null;
    }
  }, []);

  const value = {
    projects,
    currentProject,
    loading,
    error,
    fetchProjects,
    createProject,
    updateProject,
    deleteProject,
    archiveProject,
    unarchiveProject,
    selectProject,
    getProjectContext
  };

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
};
